/**
 * LayoutSelector Component
 *
 * Visual layout selection component for marketing blocks
 * Displays thumbnail previews of available layout options
 *
 * Props:
 * - value: Current selected layout string
 * - onChange: Callback when layout changes
 * - layouts: Array of layout objects { value, label, description, icon }
 * - blockType: Type of block (hero, feature, cta) for context
 */

import React from "react";

export default function LayoutSelector({
  value,
  onChange,
  layouts = [],
  blockType = "section",
}) {
  if (!layouts || layouts.length === 0) return null;

  return (
    <div className="layout-selector mb-4">
      <label className="block text-sm font-medium text-neutral-700 mb-2">
        Layout
      </label>
      <div className="grid grid-cols-2 gap-3">
        {layouts.map((layout) => (
          <button
            key={layout.value}
            type="button"
            onClick={() => onChange(layout.value)}
            className={`
              relative p-3 rounded border-2 transition-all
              hover:border-blue-400 hover:bg-blue-50
              ${
                value === layout.value
                  ? "border-blue-500 bg-blue-50 shadow-sm"
                  : "border-gray-200 bg-white"
              }
            `}
          >
            {/* Layout icon/preview */}
            <div className="aspect-video bg-gray-100 rounded mb-2 flex items-center justify-center">
              {layout.icon || (
                <svg
                  className="w-8 h-8 text-neutral-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                  />
                </svg>
              )}
            </div>

            {/* Layout label */}
            <div className="text-left">
              <div className="text-sm font-medium text-neutral-900">
                {layout.label}
              </div>
              {layout.description && (
                <div className="text-xs text-neutral-500 mt-1">
                  {layout.description}
                </div>
              )}
            </div>

            {/* Selected indicator */}
            {value === layout.value && (
              <div className="absolute top-2 right-2">
                <svg
                  className="w-5 h-5 text-blue-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
